import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
    FadeInOutContainer,
    FadeInOut,
} from 'wanderu-animations';
import { localize } from 'wanderu-i18n';

import { ARRIVAL_TYPE, DEPARTURE_TYPE } from '../../constants';
import DirectOrNearbyFilter from './DirectOrNearbyFilter/DirectOrNearbyFilter';
import FeatureFilter from './FeatureFilter/FeatureFilter';
import StationFilter from './StationsFilter/StationsFilter';
import TimeFilter from './TimeFilter/TimeFilter';
import TravelModeFilter from './TravelModeFilter/TravelModeFilter';

import styles from './SearchFilters.scss';

const propTypes = {
    date: PropTypes.string,
    filters: PropTypes.object,
    isArrivalStationsFiltered: PropTypes.bool,
    isArrivalTimingFiltered: PropTypes.bool,
    isDepartureStationsFiltered: PropTypes.bool,
    isDepartureTimingFiltered: PropTypes.bool,
    isFeaturesFiltered: PropTypes.bool,
    isTravelModesFiltered: PropTypes.bool,
    mobile: PropTypes.bool,
    onResetAllFilters: PropTypes.func,
    resetArrivalStations: PropTypes.func,
    resetArrivalTiming: PropTypes.func,
    resetDepartureStations: PropTypes.func,
    resetDepartureTiming: PropTypes.func,
    resetFeatures: PropTypes.func,
    resetTravelModes: PropTypes.func,
    searchParams: PropTypes.shape({
        destinationPlaceId: PropTypes.string,
        destinationText: PropTypes.string,
        originPlaceId: PropTypes.string,
        originText: PropTypes.string,
    }),
    setAllowNearbyActive: PropTypes.func,
    setArrivalStationActive: PropTypes.func,
    setArrivalStationOnlyActive: PropTypes.func,
    setArrivalTimingRange: PropTypes.func,
    setCarrierActive: PropTypes.func,
    setCarrierOnlyActive: PropTypes.func,
    setDepartureStationActive: PropTypes.func,
    setDepartureStationOnlyActive: PropTypes.func,
    setDepartureTimingRange: PropTypes.func,
    setDirectOnlyActive: PropTypes.func,
    setFeatureActive: PropTypes.func,
    setTravelModeActive: PropTypes.func,
    setTravelModeOnlyActive: PropTypes.func,
    showReset: PropTypes.bool,
    t: PropTypes.func,
    totalTrips: PropTypes.number,
};

const defaultProps = {
    date: '',
    filters: {},
    isArrivalTimingFiltered: false,
    isDepartureTimingFiltered: false,
    isFeaturesFiltered: false,
    isTravelModesFiltered: false,
    mobile: false,
    onResetAllFilters: () => undefined,
    showReset: false,
    totalTrips: 0,
};

function SearchFilters(props) {
    const {
        date,
        filters,
        isArrivalStationsFiltered,
        isArrivalTimingFiltered,
        isDepartureStationsFiltered,
        isDepartureTimingFiltered,
        isFeaturesFiltered,
        isTravelModesFiltered,
        mobile,
        onResetAllFilters,
        resetArrivalStations,
        resetArrivalTiming,
        resetDepartureStations,
        resetDepartureTiming,
        resetFeatures,
        resetTravelModes,
        searchParams,
        setAllowNearbyActive,
        setArrivalStationActive,
        setArrivalStationOnlyActive,
        setArrivalTimingRange,
        setCarrierActive,
        setCarrierOnlyActive,
        setDepartureStationActive,
        setDepartureStationOnlyActive,
        setDepartureTimingRange,
        setDirectOnlyActive,
        setFeatureActive,
        setTravelModeActive,
        setTravelModeOnlyActive,
        showReset,
        t,
        totalTrips,
    } = props;
    const arriveStations = filters.stations && filters.stations[ARRIVAL_TYPE];
    const departStations = filters.stations && filters.stations[DEPARTURE_TYPE];

    return (
        <div
            className={classNames(
                styles.searchFilters,
                { [styles.mobile]: mobile },
            )}
        >
            <div className={styles.resetContainer}>
                <FadeInOutContainer
                    animateHeight={false}
                    className={styles.fadeContainer}
                    duration={250}
                >
                    <FadeInOut show={showReset}>
                        <span
                            className={styles.resetButton}
                            onClick={onResetAllFilters}
                            role="button"
                        >
                            {t('reset-all')}
                        </span>
                    </FadeInOut>
                </FadeInOutContainer>
            </div>
            <DirectOrNearbyFilter
                allowNearby={filters.allowNearby}
                directOnly={filters.directOnly}
                onToggleAllowNearby={setAllowNearbyActive}
                onToggleDirectOnly={setDirectOnlyActive}
                searchParams={searchParams}
                totalTrips={totalTrips}
            />
            <TravelModeFilter
                allowNearby={filters.allowNearby}
                carriers={filters.carriers}
                isTravelModesFiltered={isTravelModesFiltered}
                onOnlyCarrierClick={setCarrierOnlyActive}
                onOnlyTravelModeClick={setTravelModeOnlyActive}
                onResetTravelModes={resetTravelModes}
                onToggleAllowNearby={setAllowNearbyActive}
                onToggleCarrier={setCarrierActive}
                onToggleTravelMode={setTravelModeActive}
                searchParams={searchParams}
                totalTrips={totalTrips}
                travelModes={filters.travelModes}
            />
            <TimeFilter
                date={date}
                isArrivalTimingFiltered={isArrivalTimingFiltered}
                isDepartureTimingFiltered={isDepartureTimingFiltered}
                onArrivalTimingChange={setArrivalTimingRange}
                onArrivalTimingReset={resetArrivalTiming}
                onDepartureTimingChange={setDepartureTimingRange}
                onDepartureTimingReset={resetDepartureTiming}
                searchParams={searchParams}
                timing={filters.timing}
                totalTrips={totalTrips}
            />
            <StationFilter
                arriveStations={arriveStations}
                departStations={departStations}
                isArrivalStationsFiltered={isArrivalStationsFiltered}
                isDepartureStationsFiltered={isDepartureStationsFiltered}
                resetArrivalStations={resetArrivalStations}
                resetDepartureStations={resetDepartureStations}
                searchParams={searchParams}
                setArrivalStationActive={setArrivalStationActive}
                setArrivalStationOnlyActive={setArrivalStationOnlyActive}
                setDepartureStationActive={setDepartureStationActive}
                setDepartureStationOnlyActive={setDepartureStationOnlyActive}
                totalTrips={totalTrips}
            />
            <FeatureFilter
                features={filters.features}
                isFeaturesFiltered={isFeaturesFiltered}
                onFeatureReset={resetFeatures}
                onToggleFeature={setFeatureActive}
                searchParams={searchParams}
                totalTrips={totalTrips}
            />
        </div>
    );
}

SearchFilters.propTypes = propTypes;
SearchFilters.defaultProps = defaultProps;

export { SearchFilters };
export default localize(SearchFilters, 'baseApp');
